<template>
  <div class="filter-slider border border-gray-100 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 overflow-hidden" :data-filter-name="filter.name">
    <div class="p-4">
      <!-- Weekly Payment specific UI -->
      <div v-if="props.filter.name === 'perweek'" class="mb-3">
        <!-- Weekly payment display -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Weekly Payment</div>
            <div class="text-2xl font-bold text-primary dark:text-primary-300">${{ formatPrice(weeklyPayment) }}<span class="text-sm font-normal">/week</span></div>
          </div>
          <div class="bg-primary/10 text-primary dark:bg-primary-900/20 dark:text-primary-300 rounded-full px-3 py-1 text-xs font-medium">
            {{ interestRate }}% | {{ loanTerm }} years
          </div>
        </div>

        <!-- Maximum qualifying price -->
        <div class="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 mb-4 flex items-center justify-between">
          <div class="text-xs text-gray-600 dark:text-gray-300">
            Maximum vehicle price:
          </div>
          <div class="text-sm font-bold text-gray-900 dark:text-white">
            $63,243.00
          </div>
        </div>

        <!-- Loan amount slider -->
        <div class="space-y-2 mb-3">
          <div class="flex items-center justify-between">
            <label class="text-xs font-medium text-gray-600 dark:text-gray-300">Loan Amount</label>
            <span class="text-xs font-bold text-gray-800 dark:text-gray-100">${{ formatPrice(loanAmount) }}</span>
          </div>
          <input
            v-model.number="loanAmount"
            type="range"
            :min="10000"
            :max="100000"
            step="1000"
            class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary"
            @input="() => { isUserInteraction = true; updateLoanCalculation() }"
          />
          <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>$10,888.00</span>
            <span>$115,597.00</span>
          </div>
        </div>

        <!-- Deposit percentage -->
        <div class="space-y-2 mb-3">
          <div class="flex items-center justify-between">
            <label class="text-xs font-medium text-gray-600 dark:text-gray-300">Deposit</label>
            <span class="text-xs font-bold text-gray-800 dark:text-gray-100">{{ depositPercentage }}% (${{ formatPrice(calculateCurrentDeposit()) }})</span>
          </div>
          <input
            v-model.number="depositPercentage"
            type="range"
            min="0"
            max="50"
            step="5"
            class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary"
            @input="() => { isUserInteraction = true; updateLoanCalculation() }"
          />
          <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>0%</span>
            <span>50%</span>
          </div>
        </div>

        <!-- Interest Rate -->
        <div class="space-y-2 mb-3">
          <div class="flex items-center justify-between">
            <label class="text-xs font-medium text-gray-600 dark:text-gray-300">Interest Rate</label>
            <span class="text-xs font-bold text-gray-800 dark:text-gray-100">{{ interestRate }}%</span>
          </div>
          <input
            v-model.number="interestRate"
            type="range"
            min="1"
            max="20"
            step="0.01"
            class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary"
            @input="() => { isUserInteraction = true; updateLoanCalculation() }"
          />
          <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>1.00%</span>
            <span>20.00%</span>
          </div>
        </div>

        <!-- Loan Term -->
        <div class="space-y-2 mb-3">
          <div class="flex items-center justify-between">
            <label class="text-xs font-medium text-gray-600 dark:text-gray-300">Loan Term</label>
            <span class="text-xs font-bold text-gray-800 dark:text-gray-100">{{ loanTerm }} years ({{ loanTerm * 12 }} months)</span>
          </div>
          <div class="flex gap-2">
            <button
              v-for="term in [1, 3, 5, 7]"
              :key="term"
              class="flex-1 text-xs px-3 py-1.5 rounded-md border transition-all font-medium"
              :class="{
                'bg-white dark:bg-gray-800 text-primary dark:text-primary-300 shadow-xs border-gray-100 dark:border-gray-700 ring-1 ring-primary/10 dark:ring-primary-700/20': loanTerm === term,
                'bg-gray-50 dark:bg-gray-900 border-gray-200 text-gray-700 hover:bg-white dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800': loanTerm !== term
              }"
              @click="setLoanTerm(term)"
            >
              {{ term }} {{ term === 1 ? 'year' : 'years' }}
            </button>
          </div>
        </div>

        <!-- Disclaimer -->
        <div class="text-xs text-gray-500 dark:text-gray-400 mt-4 italic">
          * This is a basic calculator for estimation purposes only. Actual loan terms may vary. Please consult with a finance professional.
        </div>
      </div>

      <!-- Regular filter UI for non-perweek filters -->
      <div v-else>
        <h3 class="font-medium text-base mb-2">{{ props.filter.displayName || capitalizeFirstLetter(props.filter.name) }}</h3>

        <div class="flex justify-between text-sm mb-1">
          <span>{{ formatValue(localValue[0]) }}</span>
          <span>{{ formatValue(localValue[1]) }}</span>
        </div>

        <!-- Shadcn Slider component -->
        <Slider
          v-model="localValue"
          :min="range.min"
          :max="range.max"
          :step="range.step"
          class="mb-3"
          @update:model-value="handleSliderChange"
        />

        <!-- Simplified min/max display -->
        <div class="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
          <span>${{ formatValue(localValue[0]).replace('$', '') }}</span>
          <span>${{ formatValue(localValue[1]).replace('$', '') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Slider } from '~/layers/base/components/ui/slider'
import { useFilters } from '~/layers/base/composables/useFilters'
import { useRoute } from '#imports'

const props = defineProps({
  filter: {
    type: Object,
    required: true
  },
  activeValue: {
    type: [Object, String],
    default: () => null
  }
})

const emit = defineEmits(['update:value', 'clear'])

// Get filter functions at setup time
const { trackFilterInteraction } = useFilters()

// Weekly payment calculator state
const loanAmount = ref(63243)
const interestRate = ref(9.99)
const loanTerm = ref(5)
const depositPercentage = ref(10)
const weeklyPayment = ref(278.33)

// Flag to track if loan calculation is from user interaction
const isUserInteraction = ref(false)

// Get range data from filter
const range = computed(() => {
  if (!props.filter) {
    console.error(`[FilterSlider] Filter is undefined or null`);
    return {
      min: 0,
      max: 100,
      step: 1,
      unit: ''
    }
  }

  if (!props.filter.data) {
    console.error(`[FilterSlider] [${props.filter.name}] Filter data is missing:`, props.filter);
    return {
      min: 0,
      max: 100,
      step: 1,
      unit: ''
    }
  }

  console.log(`[FilterSlider] [${props.filter.name}] Getting filter data:`, props.filter.data);

  return {
    min: props.filter.data.min,
    max: props.filter.data.max,
    step: props.filter.data.step || 1,
    unit: props.filter.data.unit || ''
  }
})

// Initialize local value
const localValue = ref<[number, number]>([range.value.min, range.value.max])

// Check URL parameters for perweek filter
const route = useRoute()
if (props.filter.name === 'perweek' && route.query.perweek_min && route.query.perweek_max) {
  const min = parseInt(route.query.perweek_min as string, 10)
  const max = parseInt(route.query.perweek_max as string, 10)

  if (!isNaN(min) && !isNaN(max)) {
    console.log(`[FilterSlider] [${props.filter.name}] Initializing from URL parameters:`, min, max)
    localValue.value = [min, max]

    // Emit the value to update the store
    emit('update:value', {
      min: min,
      max: max
    })
  }
}

// Update local value when active value changes
watch(() => props.activeValue, (newValue) => {
  if (newValue && typeof newValue === 'object' && 'min' in newValue && 'max' in newValue) {
    console.log(`[${props.filter.name}] Active value changed:`, newValue);
    localValue.value = [newValue.min, newValue.max]
  } else {
    // Handle empty string or null/undefined case
    localValue.value = [range.value.min, range.value.max]
  }
}, { immediate: true })

// Watch for changes in the filter data itself (important for dynamic ranges)
watch(() => props.filter.data, (newData) => {
  if (newData && typeof newData === 'object' && 'min' in newData && 'max' in newData) {
    console.log(`[${props.filter.name}] Filter data changed:`, newData);

    // Check if the active value is within the new range
    if (props.activeValue && typeof props.activeValue === 'object' && 'min' in props.activeValue && 'max' in props.activeValue) {
      // Only update if the active value is outside the new range
      if (props.activeValue.min < newData.min || props.activeValue.max > newData.max) {
        const newMin = Math.max(props.activeValue.min, newData.min);
        const newMax = Math.min(props.activeValue.max, newData.max);

        localValue.value = [newMin, newMax];
        emit('update:value', { min: newMin, max: newMax });
      }
    } else {
      // No active value, use the new range as default
      // This matches what FilterSection now returns as default
      localValue.value = [newData.min, newData.max];
      // Don't emit here - let the parent handle the undefined state
    }
  }
}, { deep: true })

// Check if this is a price filter
const isPriceFilter = computed(() => {
  return props.filter.name === 'price' || props.filter.name === 'perweek'
})

// Format value with unit for display
const formatValue = (value: number) => {
  if (isPriceFilter.value) {
    return `$${value.toLocaleString()}`
  } else if (props.filter.name === 'kms') {
    return `${value.toLocaleString()} km`
  }
  return `${value}${range.value.unit}`
}

// Format value for input field (no thousand separators)
const formatInputValue = (value: number) => {
  return value.toString()
}

// Handle slider change
const handleSliderChange = () => {
  // Only track filter interaction for direct slider changes if they're significant
  const defaultMin = range.value.min
  const defaultMax = range.value.max
  const totalRange = defaultMax - defaultMin

  // Current range as percentage of full range
  const currentRange = localValue.value[1] - localValue.value[0]
  const percentageOfFullRange = totalRange > 0 ? (currentRange / totalRange) * 100 : 100

  // Calculate how far min/max are from defaults as percentages
  const minPercentageFromDefault = totalRange > 0 ? Math.abs(localValue.value[0] - defaultMin) / totalRange * 100 : 0
  const maxPercentageFromDefault = totalRange > 0 ? Math.abs(localValue.value[1] - defaultMax) / totalRange * 100 : 0

  // Only track significant changes:
  // - Range narrowed to less than 80% of default
  // - Min/max shifted by more than 10% of total range
  const significantRangeChange = percentageOfFullRange < 80
  const significantMinMaxShift = minPercentageFromDefault > 10 || maxPercentageFromDefault > 10

  if (significantRangeChange || significantMinMaxShift) {
    trackFilterInteraction(props.filter.name)
    console.log(`Tracking significant ${props.filter.name} change: range=${percentageOfFullRange.toFixed(2)}%, min=${minPercentageFromDefault.toFixed(2)}%, max=${maxPercentageFromDefault.toFixed(2)}%`)
  }

  updateValue()
}

// Handle min input
const handleMinInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  const value = input.value

  if (isPriceFilter.value) {
    // For price, allow only digits
    input.value = value.replace(/[^\d]/g, '')
  }
}

// Handle max input
const handleMaxInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  const value = input.value

  if (isPriceFilter.value) {
    // For price, allow only digits
    input.value = value.replace(/[^\d]/g, '')
  }
}

// Handle min blur
const handleMinBlur = (event: Event) => {
  const input = event.target as HTMLInputElement
  const value = parseInt(input.value) || range.value.min

  // Ensure min <= max
  localValue.value[0] = Math.min(value, localValue.value[1])

  // Only track interactions if the change is significant
  const defaultMin = range.value.min
  const defaultMax = range.value.max
  const totalRange = defaultMax - defaultMin

  // Calculate percentage change from default
  const percentFromDefault = Math.abs(value - defaultMin) / totalRange * 100

  // Only track if user changed min value significantly (more than 10% of range)
  if (percentFromDefault > 10) {
    trackFilterInteraction(props.filter.name)
    console.log(`Tracking ${props.filter.name} min change: ${percentFromDefault.toFixed(2)}% from default`)
  }

  updateValue()
}

// Handle max blur
const handleMaxBlur = (event: Event) => {
  const input = event.target as HTMLInputElement
  const value = parseInt(input.value) || range.value.max

  // Ensure max >= min
  localValue.value[1] = Math.max(value, localValue.value[0])

  // Only track interactions if the change is significant
  const defaultMin = range.value.min
  const defaultMax = range.value.max
  const totalRange = defaultMax - defaultMin

  // Calculate percentage change from default
  const percentFromDefault = Math.abs(value - defaultMax) / totalRange * 100

  // Only track if user changed max value significantly (more than 10% of range)
  if (percentFromDefault > 10) {
    trackFilterInteraction(props.filter.name)
    console.log(`Tracking ${props.filter.name} max change: ${percentFromDefault.toFixed(2)}% from default`)
  }

  updateValue()
}

// Update value when slider changes
const updateValue = () => {
  // Ensure min <= max
  if (localValue.value[0] > localValue.value[1]) {
    localValue.value[0] = localValue.value[1]
  }

  // Check if value is different from default
  if (localValue.value[0] === range.value.min && localValue.value[1] === range.value.max) {
    console.log(`[FilterSlider] [${props.filter.name}] Clearing filter, values are at default range`)
    emit('clear')
  } else {
    // Log for perweek filter specifically
    if (props.filter.name === 'perweek') {
      console.log(`[FilterSlider] Emitting perweek update with: min=${localValue.value[0]}, max=${localValue.value[1]}`)
    }

    emit('update:value', {
      min: localValue.value[0],
      max: localValue.value[1]
    })
  }
}

// Set loan term
const setLoanTerm = (term: number) => {
  loanTerm.value = term
  isUserInteraction.value = true
  updateLoanCalculation()
}

// Calculate current deposit based on loan amount and percentage
const calculateCurrentDeposit = () => {
  return Math.round((loanAmount.value * depositPercentage.value) / 100)
}

// Update loan calculation with more reactive updates
const updateLoanCalculation = () => {
  console.log("Updating loan calculation with:", {
    loanAmount: loanAmount.value,
    depositPercentage: depositPercentage.value,
    interestRate: interestRate.value,
    loanTerm: loanTerm.value
  })

  try {
    // Calculate actual loan after deposit
    const deposit = (loanAmount.value * depositPercentage.value) / 100
    const principal = loanAmount.value - deposit

    // Convert annual interest rate to weekly (annual rate / 52)
    const weeklyRate = interestRate.value / 100 / 52

    // Total number of weekly payments over the loan term (years * 52 weeks)
    const totalPayments = loanTerm.value * 52

    // Prevent division by zero or other calculation errors
    if (weeklyRate <= 0 || totalPayments <= 0 || principal <= 0) {
      console.warn("Invalid calculation parameters", { weeklyRate, totalPayments, principal })
      return
    }

    // Standard loan payment formula: P * r * (1 + r)^n / ((1 + r)^n - 1)
    const powerTerm = Math.pow(1 + weeklyRate, totalPayments)
    const paymentAmount = principal * weeklyRate * powerTerm / (powerTerm - 1)

    // Check for valid result
    if (!isNaN(paymentAmount) && isFinite(paymentAmount) && paymentAmount > 0) {
      weeklyPayment.value = Math.round(paymentAmount * 100) / 100
      console.log("Weekly payment calculated:", weeklyPayment.value)
    } else {
      console.warn("Invalid payment calculation result:", paymentAmount)
    }
  } catch (e) {
    console.error("Error calculating payment:", e)
  }

  // Update weekly payment filter range ONLY when user interacts with loan calculator
  if (props.filter.name === 'perweek' && isUserInteraction.value) {
    // Track the interaction
    trackFilterInteraction(props.filter.name)

    // Update the filter - use current weekly payment value
    const newMin = Math.max(1, Math.floor(weeklyPayment.value * 0.7))
    const newMax = Math.ceil(weeklyPayment.value * 1.3)

    console.log(`[FilterSlider] User interaction: Updating perweek filter range: min=${newMin}, max=${newMax}`)

    // Update the local value
    localValue.value = [newMin, newMax]

    // Emit the updated value to the parent
    console.log(`[FilterSlider] Emitting perweek update:value event with: min=${newMin}, max=${newMax}`)
    emit('update:value', {
      min: newMin,
      max: newMax
    })
    
    // Reset the flag after handling
    isUserInteraction.value = false
  }
}

// Add a method to calculate maximum vehicle price based on weekly payment
const calculateMaxVehiclePrice = () => {
  // Using fixed values from the mockup
  const interestRate = 9.99
  const loanTermYears = 5
  const deposit = 10 // 10% deposit

  // Weekly payment (using the max value)
  const weeklyPayment = localValue.value[1]

  // Convert annual interest rate to weekly
  const weeklyRate = interestRate / 100 / 52

  // Total number of payments
  const totalPayments = loanTermYears * 52

  // Reverse the loan formula to find the maximum principal
  // P = payment * ((1 + r)^n - 1) / (r * (1 + r)^n)
  const maxPrincipal = weeklyPayment * (Math.pow(1 + weeklyRate, totalPayments) - 1) / (weeklyRate * Math.pow(1 + weeklyRate, totalPayments))

  // Add back the deposit percentage to get total vehicle price
  const maxPrice = maxPrincipal / (1 - deposit / 100)
  return Math.round(maxPrice)
}

// Format price for display without /week suffix
const formatPrice = (value: number) => {
  return value.toLocaleString()
}

// Add a method to calculate loan amount
const calculateLoanAmount = () => {
  // Get the mid-point of the slider range as the loan amount
  const weeklyPayment = (localValue.value[0] + localValue.value[1]) / 2

  // Using fixed values from the mockup
  const interestRate = 9.99
  const loanTermYears = 5

  // Convert annual interest rate to weekly
  const weeklyRate = interestRate / 100 / 52

  // Total number of payments
  const totalPayments = loanTermYears * 52

  // Reverse the loan formula to find the principal
  // P = payment * ((1 + r)^n - 1) / (r * (1 + r)^n)
  const principal = weeklyPayment * (Math.pow(1 + weeklyRate, totalPayments) - 1) / (weeklyRate * Math.pow(1 + weeklyRate, totalPayments))

  return Math.round(principal)
}

// Add a method to calculate deposit amount
const calculateDeposit = () => {
  const loanAmount = calculateLoanAmount()
  const deposit = loanAmount * 0.1 // 10% deposit
  return Math.round(deposit)
}

// Add a function to capitalize first letter
const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}
</script>
